import { createFileRoute, Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { ATTENDANCE_STATUSES, type AttendanceStatus } from '@academy/config'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Card, CardBody } from '../components/Card.js'
import { Button } from '../components/Button.js'
import { Field, Input } from '../components/Field.js'
import { Icon } from '../components/Icon.js'
import { Empty } from '../components/Empty.js'
import { UrgencyBadge } from '../components/UrgencyBadge.js'

export const Route = createFileRoute('/_app/attendance/$classId')({
  component: AttendanceGrid,
})

type Status = AttendanceStatus

interface ClassRow {
  id: string
  name: string
  academicYear: string | null
}

interface StudentRow {
  id: string
  fullName: string
  classes: { id: string; name: string }[]
  isActive: boolean
}

interface AttendanceRow {
  id: string
  studentId: string
  status: Status
  notes: string | null
}

interface UpcomingExam {
  id: string
  studentId: string
  studentName: string
  title: string
  startDate: string
  endDate: string | null
  daysUntil: number
}

const STATUS_ACTIVE_CLS: Record<Status, string> = {
  present: 'bg-success text-white',
  absent: 'bg-danger text-white',
  late: 'bg-warning text-white',
  excused: 'bg-fg-muted text-white',
}

function AttendanceGrid() {
  const { classId } = Route.useParams()
  const qc = useQueryClient()

  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [draft, setDraft] = useState<Map<string, { status: Status; notes: string }>>(
    new Map(),
  )
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<string | null>(null)

  const klass = useQuery({
    queryKey: ['classes'],
    queryFn: async () => {
      const res = await api<ClassRow[]>('/api/v1/classes')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    select: (rows) => rows.find((c) => c.id === classId),
  })

  const students = useQuery({
    queryKey: ['students'],
    queryFn: async () => {
      const res = await api<StudentRow[]>('/api/v1/students')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const attendance = useQuery({
    queryKey: ['attendance', classId, date],
    queryFn: async () => {
      const res = await api<AttendanceRow[]>(
        `/api/v1/attendance?classId=${classId}&date=${date}`,
      )
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  // School exams for this class's roster in the next 14 days (independent of the
  // picked attendance date) so the teacher sees who has a school exam coming up.
  const upcomingExams = useQuery({
    queryKey: ['class-upcoming-exams', classId],
    queryFn: async () => {
      const res = await api<UpcomingExam[]>(
        `/api/v1/student-exams/upcoming?classId=${classId}&days=14`,
      )
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const roster = useMemo(
    () =>
      (students.data ?? [])
        .filter((s) => s.isActive && s.classes.some((cls) => cls.id === classId))
        .sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [students.data, classId],
  )

  useEffect(() => {
    if (!attendance.data || roster.length === 0) return
    const next = new Map<string, { status: Status; notes: string }>()
    const existingMap = new Map(attendance.data.map((a) => [a.studentId, a]))
    for (const s of roster) {
      const existing = existingMap.get(s.id)
      next.set(s.id, {
        status: existing?.status ?? 'present',
        notes: existing?.notes ?? '',
      })
    }
    setDraft(next)
  }, [attendance.data, roster])

  const setStatus = (studentId: string, status: Status) => {
    setDraft((curr) => {
      const next = new Map(curr)
      const existing = next.get(studentId) ?? { status: 'present', notes: '' }
      next.set(studentId, { ...existing, status })
      return next
    })
  }

  const setNotes = (studentId: string, notes: string) => {
    setDraft((curr) => {
      const next = new Map(curr)
      const existing = next.get(studentId) ?? { status: 'present', notes: '' }
      next.set(studentId, { ...existing, notes })
      return next
    })
  }

  const setAll = (status: Status) => {
    setDraft((curr) => {
      const next = new Map(curr)
      for (const s of roster) {
        const existing = next.get(s.id) ?? { status: 'present', notes: '' }
        next.set(s.id, { ...existing, status })
      }
      return next
    })
  }

  const save = useMutation({
    mutationFn: async () => {
      const entries = roster.map((s) => {
        const d = draft.get(s.id) ?? { status: 'present' as Status, notes: '' }
        return {
          studentId: s.id,
          status: d.status,
          notes: d.notes ? d.notes : undefined,
        }
      })
      const res = await api<{ marked: number; newlyAbsent: number }>(
        '/api/v1/attendance/bulk',
        {
          method: 'POST',
          body: JSON.stringify({ classId, date, entries }),
        },
      )
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['attendance', classId, date] })
      setSavedAt(new Date().toLocaleTimeString())
      console.info('attendance saved', data)
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  })

  const counts = useMemo(() => {
    const c = { present: 0, absent: 0, late: 0, excused: 0 }
    for (const s of roster) {
      const d = draft.get(s.id)
      c[d?.status ?? 'present'] += 1
    }
    return c
  }, [draft, roster])

  return (
    <div className="space-y-6 pb-24 md:pb-6">
      <PageHeader
        title={klass.data?.name ?? 'Class attendance'}
        description={
          klass.data?.academicYear
            ? `Academic year ${klass.data.academicYear}`
            : "Mark today's attendance for the whole class."
        }
        actions={
          <Link to="/my-classes" className="text-sm text-fg-muted hover:text-fg">
            ← Classes
          </Link>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardBody className="space-y-2">
            <Field label="Date">
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </Field>
            <p className="text-xs text-fg-subtle">
              Roster: <strong className="text-fg">{roster.length}</strong> students
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-xs text-fg-subtle">Present</p>
            <p className="mt-1 text-2xl font-semibold text-success tabular-nums">
              {counts.present}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-xs text-fg-subtle">Absent</p>
            <p className="mt-1 text-2xl font-semibold text-danger tabular-nums">
              {counts.absent}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-xs text-fg-subtle">Late</p>
            <p className="mt-1 text-2xl font-semibold text-warning tabular-nums">
              {counts.late}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-xs text-fg-subtle">Excused</p>
            <p className="mt-1 text-2xl font-semibold text-fg-muted tabular-nums">
              {counts.excused}
            </p>
          </CardBody>
        </Card>
      </div>

      {upcomingExams.data && upcomingExams.data.length > 0 ? (
        <Card>
          <CardBody className="space-y-2">
            <div className="flex items-center gap-2">
              <Icon name="calendar" size={16} className="text-fg-muted" />
              <h3 className="text-sm font-semibold text-fg">Upcoming school exams</h3>
              <span className="text-xs text-fg-subtle">next 14 days</span>
            </div>
            <ul className="divide-y divide-border">
              {upcomingExams.data.map((ex) => (
                <li key={ex.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span className="min-w-0">
                    <span className="font-medium text-fg">{ex.studentName}</span>
                    <span className="mt-0.5 block text-xs text-fg-subtle">
                      {ex.title} ·{' '}
                      {new Date(ex.startDate).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                      })}
                      {ex.endDate
                        ? ` → ${new Date(ex.endDate).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                          })}`
                        : ''}
                    </span>
                  </span>
                  <UrgencyBadge daysUntil={ex.daysUntil} />
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      <div className="flex items-center gap-2 text-sm">
        <span className="text-fg-subtle">Quick fill:</span>
        <button
          onClick={() => setAll('present')}
          className="rounded-md border border-border px-3 py-1.5 text-xs hover:bg-surface-muted"
        >
          All present
        </button>
        <button
          onClick={() => setAll('absent')}
          className="rounded-md border border-border px-3 py-1.5 text-xs hover:bg-surface-muted"
        >
          All absent
        </button>
      </div>

      {/* Mobile: stacked cards (one per student). Desktop: table. */}
      {roster.length === 0 ? (
        <Card>
          <Empty
            icon="users"
            title="No active students in this class."
            description="Ask your academy admin to assign students to this class."
          />
        </Card>
      ) : (
        <>
          <ul className="md:hidden space-y-2">
            {roster.map((s) => {
              const d = draft.get(s.id) ?? { status: 'present' as Status, notes: '' }
              return (
                <li
                  key={s.id}
                  className="rounded-lg border border-border bg-surface p-3 shadow-sm"
                >
                  <p className="font-medium text-fg mb-2">{s.fullName}</p>
                  <div className="grid grid-cols-4 gap-2">
                    {ATTENDANCE_STATUSES.map((st) => {
                      const active = d.status === st
                      return (
                        <button
                          key={st}
                          type="button"
                          onClick={() => setStatus(s.id, st)}
                          aria-pressed={active}
                          className={`min-h-[44px] rounded-md border text-sm font-medium capitalize transition-colors ${
                            active
                              ? `${STATUS_ACTIVE_CLS[st]} border-transparent`
                              : 'border-border bg-surface text-fg-muted hover:bg-surface-muted'
                          }`}
                        >
                          {st}
                        </button>
                      )
                    })}
                  </div>
                  <input
                    value={d.notes}
                    onChange={(e) => setNotes(s.id, e.target.value)}
                    placeholder="Add a note (optional)"
                    className="mt-2 w-full rounded-md border border-border bg-surface px-2.5 py-2 text-sm text-fg placeholder-fg-faint focus:outline-none focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]"
                  />
                </li>
              )
            })}
          </ul>

          <Card className="hidden md:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface-muted text-left">
                    <th className="px-4 py-2 text-xs font-medium uppercase tracking-wide text-fg-subtle">
                      Student
                    </th>
                    <th className="px-4 py-2 text-xs font-medium uppercase tracking-wide text-fg-subtle">
                      Status
                    </th>
                    <th className="px-4 py-2 text-xs font-medium uppercase tracking-wide text-fg-subtle">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {roster.map((s) => {
                    const d = draft.get(s.id) ?? { status: 'present' as Status, notes: '' }
                    return (
                      <tr key={s.id} className="border-b border-border last:border-b-0">
                        <td className="px-4 py-2 font-medium text-fg">{s.fullName}</td>
                        <td className="px-4 py-2">
                          <div className="inline-flex rounded-md border border-border overflow-hidden">
                            {ATTENDANCE_STATUSES.map((st) => {
                              const active = d.status === st
                              return (
                                <button
                                  key={st}
                                  type="button"
                                  onClick={() => setStatus(s.id, st)}
                                  aria-pressed={active}
                                  className={`px-3 py-1.5 text-xs capitalize transition-colors ${
                                    active
                                      ? STATUS_ACTIVE_CLS[st]
                                      : 'bg-surface text-fg-muted hover:bg-surface-muted'
                                  }`}
                                >
                                  {st}
                                </button>
                              )
                            })}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <input
                            value={d.notes}
                            onChange={(e) => setNotes(s.id, e.target.value)}
                            placeholder="—"
                            className="w-full bg-transparent border-0 px-0 py-1 text-sm text-fg placeholder-fg-faint focus:outline-none focus:ring-0"
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* Sticky save bar — fixed on mobile, inline on desktop */}
      <div className="fixed bottom-0 left-0 right-0 md:static md:left-auto md:right-auto z-40 border-t border-border bg-surface md:bg-transparent md:border-0 px-4 py-3 md:px-0 md:py-0 shadow-md md:shadow-none">
        <div className="max-w-[1280px] mx-auto flex items-center justify-between gap-3">
          <p className="text-xs text-fg-subtle min-w-0 flex-1">
            {savedAt ? (
              <span className="inline-flex items-center gap-1 text-success">
                <Icon name="check" size={12} />
                Saved at {savedAt}
              </span>
            ) : (
              'Save to send absent notifications.'
            )}
            {error ? <span className="ml-2 text-danger">{error}</span> : null}
          </p>
          <Button
            onClick={() => save.mutate()}
            disabled={roster.length === 0 || save.isPending}
            loading={save.isPending}
            size="lg"
          >
            Save attendance
          </Button>
        </div>
      </div>
    </div>
  )
}
